import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Save, BarChart2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { SwissCard, SectionHeader } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const p$ = (s) => parseFloat(String(s || '0').replace(/[$,]/g, '')) || 0;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SERVICE_MIX = [
  { code: 'AT', label: 'Airport Transfer' }, { code: 'PL', label: 'Point to Lux' },
  { code: 'CC', label: 'Chauffeur Concierge' }, { code: 'EX', label: 'Exotic Driven' },
  { code: 'GR', label: 'Group / Sprinter' }, { code: 'WD', label: 'Wedding' },
  { code: 'CO', label: 'Corporate' }, { code: 'EV', label: 'Event' }, { code: 'DR', label: 'Driving Rental' },
];

const getWeekKey = () => {
  const now = new Date();
  const start = new Date(now); start.setDate(now.getDate() - now.getDay() + 1);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return `${start.toISOString().split('T')[0]}_${end.toISOString().split('T')[0]}`;
};

const WeeklyReviewTab = () => {
  const [weekKey, setWeekKey] = useState(getWeekKey());
  const [summary, setSummary] = useState({ days_active: '', bookings: '', gross: '', driver_cost: '', net: '' });
  const [days, setDays] = useState(DAYS.map(d => ({ day: d, jobs: '', gross: '', driver_cost: '', net: '', outstanding: '' })));
  const [serviceMix, setServiceMix] = useState(SERVICE_MIX.map(s => ({ ...s, jobs: '', gross: '', pct: '' })));
  const [topBookings, setTopBookings] = useState(['', '', '', '']);
  const [collections, setCollections] = useState(['', '', '', '']);
  const [notes, setNotes] = useState(['', '', '', '']);
  const [collective, setCollective] = useState({ new_drivers: '', thursday_slots: false, corey_first_refusal: false, total_paid: '' });
  const [running, setRunning] = useState({ month: '', gross_to_date: '', net_to_date: '', remaining: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/command-center/ops/weekly/${weekKey}`);
      const d = await r.json();
      if (d.summary && Object.keys(d.summary).length) setSummary(d.summary);
      if (d.days?.length) setDays(d.days);
      if (d.service_mix?.length) setServiceMix(d.service_mix);
      if (d.top_bookings?.length) setTopBookings(d.top_bookings);
      if (d.collections?.length) setCollections(d.collections);
      if (d.notes?.length) setNotes(d.notes);
      if (d.collective) setCollective(d.collective);
      if (d.running) setRunning(d.running);
    } catch {}
  }, [weekKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/command-center/ops/weekly/${weekKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary, days, service_mix: serviceMix, top_bookings: topBookings, collections, notes, collective, running }),
      });
      toast.success('Weekly review saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const totalGross = days.reduce((s, d) => s + p$(d.gross), 0);
  const totalNet = days.reduce((s, d) => s + p$(d.net), 0);
  const totalJobs = days.reduce((s, d) => s + (parseInt(d.jobs) || 0), 0);

  return (
    <div className="space-y-5" data-testid="weekly-review-tab">
      <SectionHeader
        overline="Aggregate daily data into weekly summary"
        title="Weekly Performance Review"
        action={
          <div className="flex items-center gap-2">
            <Input value={weekKey} onChange={e => setWeekKey(e.target.value)} placeholder="YYYY-MM-DD_YYYY-MM-DD" className="h-9 text-xs w-56" />
            <button onClick={save} disabled={saving} className="h-9 px-4 bg-[#E52B20] hover:bg-[#C92218] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-50">
              <Save className="h-3 w-3" /> Save
            </button>
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Days Active', key: 'days_active' }, { label: 'Total Bookings', key: 'bookings' },
          { label: 'Gross Revenue', key: 'gross' }, { label: 'Driver Cost', key: 'driver_cost' }, { label: 'Net to ACE', key: 'net' },
        ].map((f, i) => (
          <SwissCard key={i} className="p-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#666] mb-1">{f.label}</p>
            <Input value={summary[f.key] || ''} onChange={e => setSummary(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.key.includes('cost') || f.key.includes('gross') || f.key === 'net' ? '$0' : '0'} className="h-8 text-xl font-black border-0 bg-transparent p-0 focus-visible:ring-0" />
          </SwissCard>
        ))}
      </div>

      {/* Day by Day */}
      <SwissCard className="p-4">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><BarChart2 className="h-4 w-4 text-red-600" /> Day by Day</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left py-2 text-[9px] text-gray-500 uppercase">Day</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-14">Jobs</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Gross</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Driver Cost</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Net</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Outstanding</th>
            </tr></thead>
            <tbody>
              {days.map((d, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 font-semibold">{d.day}</td>
                  <td className="py-2"><Input type="number" value={d.jobs} onChange={e => setDays(p => p.map((x, j) => j === i ? { ...x, jobs: e.target.value } : x))} className="h-7 text-xs text-center w-12" /></td>
                  <td className="py-2"><Input value={d.gross} onChange={e => setDays(p => p.map((x, j) => j === i ? { ...x, gross: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                  <td className="py-2"><Input value={d.driver_cost} onChange={e => setDays(p => p.map((x, j) => j === i ? { ...x, driver_cost: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                  <td className="py-2"><Input value={d.net} onChange={e => setDays(p => p.map((x, j) => j === i ? { ...x, net: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                  <td className="py-2"><Input value={d.outstanding} onChange={e => setDays(p => p.map((x, j) => j === i ? { ...x, outstanding: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="py-2">Totals</td><td className="py-2 text-center">{totalJobs}</td><td className="py-2 text-center">{fmt(totalGross)}</td>
                <td className="py-2 text-center text-orange-600">{fmt(days.reduce((s, d) => s + p$(d.driver_cost), 0))}</td>
                <td className="py-2 text-center text-emerald-600">{fmt(totalNet)}</td>
                <td className="py-2 text-center text-red-600">{fmt(days.reduce((s, d) => s + p$(d.outstanding), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SwissCard>

      {/* Service Mix */}
      <SwissCard className="p-4">
        <h3 className="text-sm font-bold mb-3">Service Mix</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left py-2 text-[9px] text-gray-500 uppercase">Service</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-14">Jobs</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-24">Gross</th>
              <th className="text-center py-2 text-[9px] text-gray-500 uppercase w-16">%</th>
            </tr></thead>
            <tbody>{serviceMix.map((s, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="py-2"><span className="font-bold text-red-600">{s.code}</span> <span className="text-gray-500">— {s.label}</span></td>
                <td className="py-2"><Input type="number" value={s.jobs} onChange={e => setServiceMix(p => p.map((x, j) => j === i ? { ...x, jobs: e.target.value } : x))} className="h-7 text-xs text-center w-12" /></td>
                <td className="py-2"><Input value={s.gross} onChange={e => setServiceMix(p => p.map((x, j) => j === i ? { ...x, gross: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                <td className="py-2"><Input value={s.pct} onChange={e => setServiceMix(p => p.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} placeholder="0%" className="h-7 text-xs text-center w-14" /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </SwissCard>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Driven Collective */}
        <SwissCard className="p-4">
          <h3 className="text-sm font-bold mb-3">Driven Collective</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center"><span className="text-xs text-gray-500">New Drivers</span><Input type="number" value={collective.new_drivers || ''} onChange={e => setCollective(p => ({ ...p, new_drivers: e.target.value }))} className="h-7 text-xs w-16 text-center" /></div>
            <div className="flex justify-between items-center"><span className="text-xs text-gray-500">Thursday Slots Filled</span><input type="checkbox" checked={collective.thursday_slots || false} onChange={e => setCollective(p => ({ ...p, thursday_slots: e.target.checked }))} /></div>
            <div className="flex justify-between items-center"><span className="text-xs text-gray-500">Corey First-Refusal</span><input type="checkbox" checked={collective.corey_first_refusal || false} onChange={e => setCollective(p => ({ ...p, corey_first_refusal: e.target.checked }))} /></div>
            <div className="flex justify-between items-center"><span className="text-xs text-gray-500">Total Paid to Network</span><Input value={collective.total_paid || ''} onChange={e => setCollective(p => ({ ...p, total_paid: e.target.value }))} placeholder="$0" className="h-7 text-xs w-24 text-center" /></div>
          </div>
        </SwissCard>
        {/* Running Totals */}
        <SwissCard className="p-4 bg-[#0A0A0A] text-white">
          <h3 className="text-xs font-bold text-gray-400 uppercase mb-3"><TrendingUp className="inline h-3 w-3 mr-1" /> Running Totals</h3>
          <div className="space-y-2">
            {[{ label: 'Month', key: 'month', placeholder: 'April 2026' }, { label: 'Gross to Date', key: 'gross_to_date', placeholder: '$0' }, { label: 'Net to Date', key: 'net_to_date', placeholder: '$0' }, { label: 'Remaining to Goal', key: 'remaining', placeholder: '$0' }].map((f, i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{f.label}</span>
                <Input value={running[f.key] || ''} onChange={e => setRunning(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} className="h-7 text-xs w-32 bg-white/10 border-white/20 text-white text-right" />
              </div>
            ))}
          </div>
        </SwissCard>
      </div>

      {/* Lists */}
      {[{ title: 'Top Bookings', items: topBookings, setItems: setTopBookings, prefix: 'Top booking' },
        { title: 'Outstanding Collections', items: collections, setItems: setCollections, prefix: 'Collection item' },
        { title: 'Week Notes', items: notes, setItems: setNotes, prefix: 'Note' },
      ].map((section, si) => (
        <SwissCard key={si} className="p-4">
          <h3 className="text-sm font-bold mb-2">{section.title}</h3>
          <div className="space-y-1">{section.items.map((item, i) => (
            <Input key={i} value={item} onChange={e => section.setItems(p => p.map((v, j) => j === i ? e.target.value : v))} placeholder={`${section.prefix} ${i + 1}`} className="h-7 text-xs" />
          ))}</div>
        </SwissCard>
      ))}

      <div className="sticky bottom-16 sm:bottom-4 z-20 flex justify-center">
        <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 shadow-xl text-sm h-10 px-8"><Save className="h-4 w-4 mr-2" /> Save Weekly Review</Button>
      </div>
    </div>
  );
};

export default WeeklyReviewTab;
